import {mapObject} from '@benchling/js-utils';
import gql from 'graphql-tag';
import isEqual from 'react-fast-compare';

import {pluralize} from 'BenchlingUtil';
import {SchemaTag} from 'folder-items/FileConstants';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import notifications from 'notifications';
import {CreateOrUpdateServerProcessTask, EditExistingTask} from 'processes/dao/ProcessManagementDAO';
import TaskUpdateConfirmationModal from 'processes/modals/TaskUpdateConfirmationModal';
import {isTerminalStatus} from 'processes/status/TaskStatusUtil';
import {
  SubmitTasksWithConfirmationQuery,
  SubmitTasksWithConfirmationQueryVariables,
} from 'processes/tables/__generated/SubmitTasksWithConfirmationQuery';
import {getTagsToSet, TagUpdateObject} from 'schemas/tags/TagUtil';
import runModal from 'ui-library/runModal';
import pick from 'util/pick';
import {withLoadingScreen} from 'util/Promises';

import {ProcessTaskStatusLifecycleGraphQL} from '../../../graphql-fragments/processes/__generated/ProcessTaskStatusLifecycleGraphQL';

type ProcessTaskSchema = ExtractByTypename<
  SubmitTasksWithConfirmationQuery['processTaskSchema'],
  'ProcessTaskSchemaNode'
>;

type ProcessTask = ExtractByTypename<
  SubmitTasksWithConfirmationQuery['processTasks'][number],
  'ProcessTaskNode'
>;

export const SUBMIT_TASKS_WITH_CONFIRMATION_QUERY = gql`
  query SubmitTasksWithConfirmationQuery($taskIds: [StrictString!]!, $taskSchemaId: StrictString!)
  @skipAudit
  @engComponent(name: WORKFLOWS) {
    processTasks(ids: $taskIds) {
      ... on ProcessTaskNode {
        id
        fields
        lineageEdgesFrom {
          id
        }
      }
      ...InaccessibleInfoGraphQL
    }
    processTaskSchema(id: $taskSchemaId) {
      ... on ProcessTaskSchemaNode {
        id
        name
        allFields
        fields
        statusLifecycle {
          ... on ProcessTaskStatusLifecycleNode {
            ...ProcessTaskStatusLifecycleGraphQL
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('ProcessTaskStatusLifecycle').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

function standardizeTagUpdateObject(tags?: TagUpdateObject): TagUpdateObject {
  if (tags == null) {
    return {};
  }
  return mapObject(tags, (tagValue) => ({
    ...tagValue,
    url: tagValue?.url ?? '',
  }));
}

function getAlreadyMappedTasksWithUpdatedFields(
  updatedTasks: Array<EditExistingTask & {oldFields: Array<SchemaTag>; alreadyMapped: boolean}>
): Array<EditExistingTask> {
  return updatedTasks
    .filter(
      (task) =>
        task.alreadyMapped &&
        !isEqual(
          standardizeTagUpdateObject(task.fields),
          standardizeTagUpdateObject(getTagsToSet(task.oldFields))
        )
    )
    .map((task) => pick(task, 'id', 'statusId', 'fields', 'sourceType', 'actionOrigin', 'actionMethod'));
}

function getTasksWithUpdatedTerminalStatuses(
  statusLifecycle: ProcessTaskStatusLifecycleGraphQL,
  updatedTasks: Array<EditExistingTask & {oldStatusId: string}>
): Array<EditExistingTask & {statusId: string}> {
  return updatedTasks
    .filter(
      ({statusId, oldStatusId}) =>
        statusId != null && statusId !== oldStatusId && isTerminalStatus(statusId, statusLifecycle)
    )
    .map(
      (task) =>
        pick(
          task,
          'id',
          'statusId',
          'fields',
          'sourceType',
          'actionOrigin',
          'actionMethod'
        ) as EditExistingTask & {
          statusId: string;
        }
    );
}

export async function getUpdateConfirmationModalInputs(
  taskSchemaId: string,
  tasks: Array<EditExistingTask & {oldStatusId: string}>
): Promise<{
  terminalStatusTasks: Array<EditExistingTask & {statusId: string}>;
  mappedTasksWithFieldUpdates: Array<EditExistingTask>;
  processTaskSchema: ProcessTaskSchema;
} | null> {
  const taskIds = tasks.map(({id}) => id);
  const result = await doQuerySafe<
    SubmitTasksWithConfirmationQuery,
    SubmitTasksWithConfirmationQueryVariables
  >({query: SUBMIT_TASKS_WITH_CONFIRMATION_QUERY}, {variables: {taskIds, taskSchemaId}});
  if (isInaccessible(result.processTaskSchema) || result.processTasks.some((task) => isInaccessible(task))) {
    notifications.error(
      'You do not have access to perform this action. Please contact your administrator for access.'
    );
    return null;
  }

  const oldTasksById = result.processTasks.reduce(
    (acc, curr: ProcessTask) => {
      acc[curr.id] = curr;
      return acc;
    },
    {} as {[id: string]: ProcessTask}
  );

  const tasksWithOldData = tasks.map((task) => ({
    ...task,
    oldFields: oldTasksById[task.id].fields,
    alreadyMapped: oldTasksById[task.id].lineageEdgesFrom.length > 0,
  }));

  const terminalStatusTasks = getTasksWithUpdatedTerminalStatuses(
    result.processTaskSchema.statusLifecycle,
    tasksWithOldData
  );
  const mappedTasksWithFieldUpdates = getAlreadyMappedTasksWithUpdatedFields(tasksWithOldData);
  return {
    terminalStatusTasks,
    mappedTasksWithFieldUpdates,
    processTaskSchema: result.processTaskSchema,
  };
}

export const stubbableFunctions = {
  getUpdateConfirmationModalInputs,
};

function isEditExistingTask(task: CreateOrUpdateServerProcessTask): task is EditExistingTask {
  return (task as EditExistingTask).id != null;
}

export default async function submitTasksWithConfirmation({
  taskSchemaId,
  tasks,
  onRequestSubmit,
}: {
  taskSchemaId: string;
  tasks: Array<CreateOrUpdateServerProcessTask & {oldStatusId: string}>;
  onRequestSubmit: () => Promise<void>;
}): Promise<void> {
  const tasksBeingUpdated = tasks.filter((task) => isEditExistingTask(task)) as Array<
    EditExistingTask & {oldStatusId: string}
  >;
  const modalParams = await withLoadingScreen(
    stubbableFunctions.getUpdateConfirmationModalInputs(taskSchemaId, tasksBeingUpdated),
    `Saving ${pluralize(tasks.length, 'task')}`
  );
  if (modalParams == null) {
    return;
  }
  const {terminalStatusTasks, mappedTasksWithFieldUpdates, processTaskSchema} = modalParams;
  if (terminalStatusTasks.length > 0 || mappedTasksWithFieldUpdates.length > 0) {
    await runModal(TaskUpdateConfirmationModal, {
      tasksWithTerminalStatus: terminalStatusTasks,
      tasksAlreadyMappedWithUpdatedFields: mappedTasksWithFieldUpdates,
      taskSchema: processTaskSchema,
      onRequestSubmit,
    });
  } else {
    await onRequestSubmit();
  }
}
